import { AccountInfo } from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'
import { Spinner } from '@chakra-ui/react'
import { mdiContentCopy } from '@mdi/js'
import Icon from '@mdi/react'
import cloneDeep from 'lodash/cloneDeep'
import React, { useState } from 'react'
import rehypeStringify from 'rehype-stringify'
import remarkBreaks from 'remark-breaks'
import remarkGfm from 'remark-gfm'
import remarkParse from 'remark-parse'
import remarkRehype from 'remark-rehype'
import { unified } from 'unified'

import { Box } from '@/components/ui/box'
import { Tooltip } from '@/components/ui/tooltip'
import useCustomToast from '@/hooks/toasts'
import { getChatHistory } from '@/store/gigiChat/ChatAtom'
import { IConversation } from '@/types/gigiChat/Conversation.ts'

interface ICopyConversationProps {
    chatId: string
}

interface ICopyConversation extends IConversation {
    questionHtml: string
    answerHtml: string
}

function markdownToHtml(md: string) {
    return unified()
        .use(remarkParse)
        .use(remarkBreaks)
        .use(remarkRehype, { allowDangerousHtml: true })
        .use(remarkGfm)
        .use(rehypeStringify)
        .process(md)
}

function formatCitationsHtml(citations: ICopyConversation['citations']): string {
    const out = ['<ul>']

    for (const citation of citations) {
        out.push('<li>')
        if (citation.usedInCompletion) {
            out.push('<strong>')
        }
        out.push(`<a href="${citation.url}">${citation.usedInCompletion ? '★ ' : ''}${citation.name}</a>`)
        if (citation.usedInCompletion) {
            out.push('</strong>')
        }
        out.push('</li>')
    }

    out.push('</ul>')

    return out.join('')
}

function formatCitations(citations: ICopyConversation['citations']): string {
    const out = []

    for (const citation of citations) {
        if (citation.usedInCompletion) {
            out.push(`- [**${citation.name}**](${citation.url})`)
        } else {
            out.push(`- [${citation.name}](${citation.url})`)
        }
    }

    return out.join('\n')
}

function constructConversationHtmlString(conversation: ICopyConversation[], user: AccountInfo): string {
    return conversation
        .map((e) => {
            const d = new Date(e.askTimestamp)
            return `<section><em>${d.toLocaleDateString()} ${d.toLocaleTimeString()}</em>
<div>
<br>
<strong>${user.name} &lt;${user.username}&gt;:</strong> ${e.questionHtml}
</div>
<div>
<br>
<strong>GiGi:</strong> ${e.answerHtml}
</div>
${e.citations?.length ? `<div><br>⚠ <em><strong>Double-check</strong> the citations and outputs prior to using them.<em></div><div><br><em>Citations:</em> ${formatCitationsHtml(e.citations)}</div>` : ''}
</section>`
        })
        .join('\n<hr>\n')
}

function constructConversationString(conversation: ICopyConversation[], user: AccountInfo): string {
    return conversation
        .map((e) => {
            const d = new Date(e.askTimestamp)
            return `${d.toLocaleDateString()} ${d.toLocaleTimeString()}

${user.name} <${user.username}>:
${e.question}

GiGi:
${e.answer}

${e.citations?.length ? `⚠ Double-check the citations and outputs prior to using them.` : ''}

${e.citations?.length ? `Citations:\n${formatCitations(e.citations)}` : ''}
`
        })
        .join('\n-----\n')
}

const CopyConversation: React.FC<ICopyConversationProps> = ({ chatId }) => {
    const { accounts } = useMsal()
    const user = accounts[0]
    const [addToast] = useCustomToast()

    const [loading, setLoading] = useState(false)

    const copyToClipboard = async () => {
        try {
            setLoading(true)
            const history = cloneDeep(await getChatHistory(chatId)) as { data: ICopyConversation[] }
            await Promise.all(
                history.data.map(async (e) => {
                    const [q, a] = await Promise.all([markdownToHtml(e.question), markdownToHtml(e.answer)])
                    e.questionHtml = String(q)
                    e.answerHtml = String(a)
                }),
            )
            const data = [
                new ClipboardItem({
                    'text/plain': new Blob([constructConversationString(history.data, user) || ''], { type: 'text/plain' }),
                    'text/html': new Blob([constructConversationHtmlString(history.data, user) || ''], { type: 'text/html' }),
                }),
            ]

            await navigator.clipboard.write(data)
            addToast('success', 'Copied conversation to clipboard.')
        } catch (error) {
            console.error('Failed to copy content to clipboard: ', error)
            addToast('error', 'Failed to copy to clipboard, try again.')
        } finally {
            setLoading(false)
        }
    }

    return (
        <Tooltip content="Copy Conversation" variant="gsk" disabled={loading} positioning={{ placement: 'left' }}>
            <Box
                as="button"
                cursor="pointer"
                ml="auto"
                className="actionConversation"
                onClick={copyToClipboard}
                data-id="chat-copy-conversation"
            >
                {loading && (
                    <Box position="relative" width="20px" height="20px">
                        <Box position="absolute" left="0px" top="0px">
                            <Spinner w="20px" h="20px" />
                        </Box>
                    </Box>
                )}
                {!loading && <Icon path={mdiContentCopy} size="20px" />}
            </Box>
        </Tooltip>
    )
}

export default CopyConversation
